// discontinuedSheetService.js — bulk "discontinued" marking via a spreadsheet
// (Brian, 2026-08-20). The catalog shows carried SKU families (1-5/SLP/w)
// while browsing even at zero stock — but some of those items are gone for
// good, and only Brian knows which. Download the sheet of out-of-stock
// carried items, type x next to the dead ones, upload back.
//
// x  = discontinued: hidden from catalog BROWSING (search + barcode scan
//      still find them — staff returns need that), skipped by the storage
//      sheet, and auto-revived by the daily QB CSV upload if stock returns.
// xx = keep hidden (Brian, 2026-08-26): stronger — customers never see the
//      item (browse, search, scan, reorder), stock or no stock, and the
//      daily CSV does NOT revive it. Staff still find it by search/scan.
//      Only clearing the mark on this sheet brings it back.
import Papa from "papaparse";
import { db } from "../config/firebase";
import { doc, writeBatch } from "firebase/firestore";
import { invalidateProductCache } from "./dataCache";

const BATCH_SIZE = 400;

// Same carried-SKU-families rule as the catalog's browse filter.
export const CARRIED_PREFIXES = /^(?:[1-5]|slp|w)/i;

const HEADER = ["SKU", "Product Name", "Discontinued (x) / Keep hidden (xx)"];

// Aliases for the plain "discontinued" mark. "xx" is deliberately NOT here —
// it means keep-hidden now (it was an alias for x before 2026-08-26).
const YES = new Set(["x", "yes", "y", "1", "true", "o"]);
const HIDDEN = new Set(["xx", "hidden", "hide"]);

function skuNum(sku) {
  const n = parseInt(sku, 10);
  return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
}

/**
 * Download the sheet: every out-of-stock item in the carried SKU families,
 * plus anything already marked discontinued or keep-hidden (so a mark can
 * be cleared). Already-marked rows come prefilled with x / xx.
 */
export function downloadDiscontinuedSheet(products) {
  const rows = products
    .filter(p => (
      (!p.active && CARRIED_PREFIXES.test(p.sku)) || p.discontinued || p.keepHidden
    ))
    .sort((a, b) => skuNum(a.sku) - skuNum(b.sku))
    .map(p => [p.sku, p.name || "", p.keepHidden ? "xx" : p.discontinued ? "x" : ""]);

  const csv = Papa.unparse({ fields: HEADER, data: rows });
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sunjin-discontinued-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return rows.length;
}

/**
 * Apply an uploaded sheet. x (or yes/1) = discontinued; xx = keep hidden;
 * BLANK on a listed row = carried (clears either mark — that's how a mark
 * is reversed). The two marks are exclusive: setting one clears the other.
 * Only changed values are written. Returns
 * { marked, hidden, cleared, unchanged, unknownValues: [{sku, value}], unknownSkus }.
 */
export async function applyDiscontinuedSheet(file, products) {
  const text = await file.text();
  const parsed = Papa.parse(text.replace(/^﻿/, ""), { skipEmptyLines: true });
  if (parsed.errors.length > 0 && parsed.data.length === 0) {
    throw new Error("Could not read that file as a spreadsheet. Upload the CSV downloaded from this page.");
  }

  let rows = parsed.data;
  if (rows.length > 0 && String(rows[0][0] || "").trim().toLowerCase() === "sku") {
    rows = rows.slice(1);
  }
  if (rows.length === 0) {
    throw new Error("The file has no data rows. Nothing was changed.");
  }

  const bySku = new Map(products.map(p => [String(p.sku), p]));
  const changes = []; // { sku, discontinued, keepHidden }
  const report = { marked: 0, hidden: 0, cleared: 0, unchanged: 0, unknownValues: [], unknownSkus: [] };

  for (const row of rows) {
    const sku = String(row[0] ?? "").trim();
    if (!sku) continue;
    const product = bySku.get(sku);
    if (!product) {
      report.unknownSkus.push(sku);
      continue;
    }
    const raw = String(row[2] ?? "").trim().toLowerCase();
    let wantsDisc = false;
    let wantsHidden = false;
    if (raw !== "") {
      if (HIDDEN.has(raw)) wantsHidden = true;
      else if (YES.has(raw)) wantsDisc = true;
      else {
        report.unknownValues.push({ sku, value: String(row[2]).trim() });
        continue;
      }
    }
    const curDisc = !!product.discontinued;
    const curHidden = !!product.keepHidden;
    if (curDisc === wantsDisc && curHidden === wantsHidden) {
      report.unchanged++;
      continue;
    }
    changes.push({ sku, discontinued: wantsDisc, keepHidden: wantsHidden });
  }

  for (let i = 0; i < changes.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    for (const c of changes.slice(i, i + BATCH_SIZE)) {
      batch.update(doc(db, "products", c.sku), {
        discontinued: c.discontinued,
        keepHidden: c.keepHidden
      });
    }
    await batch.commit();
  }
  report.marked = changes.filter(c => c.discontinued).length;
  report.hidden = changes.filter(c => c.keepHidden).length;
  report.cleared = changes.filter(c => !c.discontinued && !c.keepHidden).length;

  if (changes.length > 0) invalidateProductCache();
  return { report, changes };
}
